import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type IntegrationConnectionTestResponse,
  type ZoomContactCenterIntegration,
  type ZoomContactCenterIntegrationConnectRequest,
  type ZoomContactCenterIntegrationRequest,
  type ZoomContactCenterQueuesResponse,
} from "@repo/app-types/entities/integration";
import type { AppDispatch } from "../../app/store.js";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The Zoom Contact Center integration comes from its own route rather than the
 * boot payload: the stored settings carry the encrypted client secret, which no
 * customer response may include, so the API returns a non-secret view instead.
 *
 * The secret travels one way only — every credential write posts it again, and
 * nothing ever reads it back, so the detail view renders a fixed mask.
 */

export interface ZoomContactCenterIntegrationResponse {
  integration: ZoomContactCenterIntegration | null;
}

const zoomTag = buildEntityTag(
  TagType.ZoomContactCenterIntegration,
  "zoom-contact-center",
);
/**
 * The queue list is read live from Zoom, so it has its own tag: saving a queue
 * selection refreshes the integration without listing Zoom's queues again,
 * while new credentials (possibly another account) refresh both.
 */
const zoomQueuesTag = buildEntityTag(
  TagType.ZoomContactCenterIntegration,
  "zoom-contact-center-queues",
);

/**
 * Queue-selection saves still in flight: the promise chain they run on, how
 * many there are, the selection the last one stores, and whether one failed.
 * Module state rather than the component's, because the queue table can
 * unmount and remount while a save is in flight.
 */
let queueSaveChain: Promise<void> = Promise.resolve();
let pendingQueueSaves = 0;
let latestPendingSelection: {
  integrationId: string;
  queueIds: readonly string[] | null;
} | null = null;
let hasQueueSaveFailed = false;

export const zoomContactCenterIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    zoomContactCenterIntegration: builder.query<
      ZoomContactCenterIntegrationResponse,
      void
    >({
      query: () => "/integrations/zoom-contact-center",
      providesTags: () => [zoomTag],
      // A reload that overlaps queue saves can carry the selection from
      // before them; put the latest one back on top. (After a failed save the
      // reload is only sent once the saves drain, so it isn't overridden.)
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const overlapsSaves = pendingQueueSaves > 0;
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        if (
          latestPendingSelection &&
          (overlapsSaves || pendingQueueSaves > 0)
        ) {
          dispatch(setCachedZoomQueueIds(latestPendingSelection));
        }
      },
    }),

    zoomContactCenterQueues: builder.query<
      ZoomContactCenterQueuesResponse,
      void
    >({
      query: () => "/integrations/zoom-contact-center/queues",
      providesTags: () => [zoomQueuesTag],
    }),

    /**
     * Probes typed credentials without storing them. Always carries the full
     * credentials because the stored secret is write-only — there is no
     * server-side test to run against a connection the customer isn't editing.
     */
    testZoomContactCenterConnection: builder.mutation<
      IntegrationConnectionTestResponse,
      ZoomContactCenterIntegrationRequest
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations/test-connection",
        body: {
          provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
          ...credentials,
        },
      }),
    }),

    /**
     * Lists the queues on the account behind typed credentials, before they
     * are saved, so the queues are chosen before the first poll. A mutation
     * for the reason the connection test is one: it carries the secret.
     */
    previewZoomContactCenterQueues: builder.mutation<
      ZoomContactCenterQueuesResponse,
      ZoomContactCenterIntegrationRequest
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations/zoom-contact-center/queues/preview",
        body: credentials,
      }),
    }),

    /** Stores the connection with its queue selection and starts the poll. */
    connectZoomContactCenterIntegration: builder.mutation<
      ZoomContactCenterIntegration,
      ZoomContactCenterIntegrationConnectRequest,
      { integration: ZoomContactCenterIntegration }
    >({
      query: (request) => ({
        method: "POST",
        url: "/integrations",
        body: {
          provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
          ...request,
        },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [zoomTag, zoomQueuesTag],
    }),

    updateZoomContactCenterIntegration: builder.mutation<
      ZoomContactCenterIntegration,
      {
        integrationId: string;
        credentials: ZoomContactCenterIntegrationRequest;
      },
      { integration: ZoomContactCenterIntegration }
    >({
      query: ({ integrationId, credentials }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: credentials,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [zoomTag, zoomQueuesTag],
    }),

    /**
     * The PATCH that stores `zoom_queue_ids` (null for every queue), sent by
     * `saveZoomQueueIdsInOrder`. No hook is exported for it.
     */
    setZoomContactCenterQueueIds: builder.mutation<
      ZoomContactCenterIntegration,
      { integrationId: string; queueIds: readonly string[] | null },
      { integration: ZoomContactCenterIntegration }
    >({
      query: ({ integrationId, queueIds }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: { zoom_queue_ids: queueIds },
      }),
      transformResponse: (response) => response.integration,
    }),

    setZoomContactCenterIntegrationEnabled: builder.mutation<
      ZoomContactCenterIntegration,
      { integrationId: string; isEnabled: boolean },
      { integration: ZoomContactCenterIntegration }
    >({
      // Shares the credential route: the union body means pausing never has to
      // carry the client secret.
      query: ({ integrationId, isEnabled }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: { is_enabled: isEnabled },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [zoomTag],
    }),

    disconnectZoomContactCenterIntegration: builder.mutation<
      void,
      { integrationId: string }
    >({
      query: ({ integrationId }) => ({
        method: "DELETE",
        url: `/integrations/${integrationId}`,
      }),
      invalidatesTags: () => [zoomTag],
    }),
  }),
});

/** Writes a queue selection into the cached integration it belongs to. */
function setCachedZoomQueueIds(selection: {
  integrationId: string;
  queueIds: readonly string[] | null;
}) {
  const { integrationId, queueIds } = selection;
  return zoomContactCenterIntegrationApi.util.updateQueryData(
    "zoomContactCenterIntegration",
    undefined,
    (draft) => {
      if (draft.integration?.id === integrationId) {
        draft.integration.settings.zoom_queue_ids = queueIds
          ? [...queueIds]
          : null;
      }
    },
  );
}

/**
 * Optimistically writes a queue selection to the cached integration, then
 * sends it behind every earlier save, one PATCH at a time in call order, so
 * the server ends on the last selection. After a failure the stored setting is
 * reloaded, but only once every queued save has landed, so the reload can't
 * bring back an older list mid-way.
 *
 * Rejects with the failure, for the caller to show. Refuses an empty list:
 * stored, it would mean every queue, which callers say with null.
 */
export function saveZoomQueueIdsInOrder(
  dispatch: AppDispatch,
  args: { integrationId: string; queueIds: readonly string[] | null },
): Promise<void> {
  const { integrationId, queueIds } = args;
  if (queueIds !== null && queueIds.length === 0) {
    return Promise.reject(
      new Error("An empty queue selection would import every queue."),
    );
  }

  dispatch(setCachedZoomQueueIds({ integrationId, queueIds }));
  pendingQueueSaves++;
  latestPendingSelection = { integrationId, queueIds };
  const saved = queueSaveChain.then(async () => {
    await dispatch(
      zoomContactCenterIntegrationApi.endpoints.setZoomContactCenterQueueIds.initiate(
        { integrationId, queueIds },
      ),
    ).unwrap();
  });
  queueSaveChain = saved
    .catch(() => {
      hasQueueSaveFailed = true;
    })
    .finally(() => {
      pendingQueueSaves--;
      if (pendingQueueSaves === 0 && hasQueueSaveFailed) {
        hasQueueSaveFailed = false;
        dispatch(
          zoomContactCenterIntegrationApi.util.invalidateTags([zoomTag]),
        );
      }
    });
  return saved;
}

/** Subscribes to the status only when Zoom CC is actually on for this company. */
export function useZoomContactCenterIntegration(enabled: boolean) {
  return zoomContactCenterIntegrationApi.useZoomContactCenterIntegrationQuery(
    enabled ? undefined : skipToken,
    { refetchOnMountOrArgChange: true },
  );
}

export const {
  useConnectZoomContactCenterIntegrationMutation,
  useDisconnectZoomContactCenterIntegrationMutation,
  usePreviewZoomContactCenterQueuesMutation,
  useSetZoomContactCenterIntegrationEnabledMutation,
  useZoomContactCenterQueuesQuery,
  useTestZoomContactCenterConnectionMutation,
  useUpdateZoomContactCenterIntegrationMutation,
} = zoomContactCenterIntegrationApi;
